import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from "recharts";

interface PipelineCoverageChartProps {
  pipelineValue: number;
  salesTarget: number;
  gap: number;
}

export function PipelineCoverageChart({ pipelineValue, salesTarget, gap }: PipelineCoverageChartProps) {
  const data = [
    {
      name: "Valor Pipeline",
      value: pipelineValue,
      color: "#3b82f6",
      textColor: "text-blue-600"
    },
    {
      name: "Meta de Ventas",
      value: salesTarget,
      color: "#64748b",
      textColor: "text-slate-600"
    }
  ];

  const coveragePercentage = ((pipelineValue / salesTarget) * 100).toFixed(1);
  const isOnTrack = pipelineValue >= salesTarget;

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
          <p className="font-semibold text-slate-900">{data.name}</p>
          <p className="text-sm text-slate-600">
            ${data.value.toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <BarChart3 className="h-5 w-5 mr-2" />
            Cobertura del Pipeline
          </div>
          <div className={`text-sm font-semibold px-3 py-1 rounded-full ${
            isOnTrack 
              ? 'bg-green-100 text-green-700' 
              : 'bg-orange-100 text-orange-700'
          }`}>
            {coveragePercentage}% de la meta
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                type="number"
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                stroke="#64748b"
              />
              <YAxis 
                type="category" 
                dataKey="name" 
                width={120}
                stroke="#64748b"
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xs text-slate-500 mb-1">Pipeline Actual</p>
              <p className="text-sm font-bold text-blue-600">
                ${pipelineValue.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Meta</p>
              <p className="text-sm font-bold text-slate-900">
                ${salesTarget.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">
                {gap > 0 ? 'Brecha' : 'Excedente'}
              </p>
              <p className={`text-sm font-bold ${gap > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                ${Math.abs(gap).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex justify-between text-xs text-slate-600 mb-1">
            <span>Progreso hacia la meta</span>
            <span className="font-semibold">{coveragePercentage}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                isOnTrack ? 'bg-green-600' : 'bg-blue-600'
              }`}
              style={{ width: `${Math.min(parseFloat(coveragePercentage), 100)}%` }}
            ></div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
